export const GUESTRIDE_STATUS = {
  NEW: 'new',
  PENDING: 'pending',
  BOOKED: 'booked',
  DRIVER_ENROUTE: 'driver_enroute',
  DRIVER_ARRIVED: 'driver_arrived',
  RIDING: 'riding',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  DELETED: 'deleted',
} as const;

export type GuestrideStatus = (typeof GUESTRIDE_STATUS)[keyof typeof GUESTRIDE_STATUS];

export const UPCOMING_STATUSES: GuestrideStatus[] = [
  GUESTRIDE_STATUS.NEW,
  GUESTRIDE_STATUS.FAILED,
];

export const ACTIVE_STATUSES: GuestrideStatus[] = [
  GUESTRIDE_STATUS.PENDING,
  GUESTRIDE_STATUS.BOOKED,
  GUESTRIDE_STATUS.DRIVER_ENROUTE,
  GUESTRIDE_STATUS.DRIVER_ARRIVED,
  GUESTRIDE_STATUS.RIDING,
];

export const HISTORY_STATUSES: GuestrideStatus[] = [
  GUESTRIDE_STATUS.COMPLETED,
  GUESTRIDE_STATUS.CANCELLED,
];

export const DELETED_STATUSES: GuestrideStatus[] = [GUESTRIDE_STATUS.DELETED];

// Statuses for which "Cancel Ride" must be hidden — the rider is in the vehicle
// or the ride is in a terminal state, so cancellation is no longer meaningful.
export const POST_PICKUP_STATUSES: GuestrideStatus[] = [
  GUESTRIDE_STATUS.RIDING,
  GUESTRIDE_STATUS.COMPLETED,
  GUESTRIDE_STATUS.CANCELLED,
  GUESTRIDE_STATUS.FAILED,
];

export type GuestRideTab = 'active' | 'upcoming' | 'history' | 'deleted';

export const TAB_DEFINITIONS: { id: number; key: GuestRideTab; label: string; icon: string }[] = [
  { id: 0, key: 'active', label: 'Active', icon: 'directions_car' },
  { id: 1, key: 'upcoming', label: 'Upcoming', icon: 'schedule' },
  { id: 2, key: 'history', label: 'History', icon: 'history' },
  { id: 3, key: 'deleted', label: 'Deleted', icon: 'delete' },
];

export const STATUS_COLOR_MAP: Record<GuestrideStatus, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  [GUESTRIDE_STATUS.NEW]: 'info',
  [GUESTRIDE_STATUS.PENDING]: 'info',
  [GUESTRIDE_STATUS.BOOKED]: 'warning',
  [GUESTRIDE_STATUS.DRIVER_ENROUTE]: 'warning',
  [GUESTRIDE_STATUS.DRIVER_ARRIVED]: 'warning',
  [GUESTRIDE_STATUS.RIDING]: 'warning',
  [GUESTRIDE_STATUS.COMPLETED]: 'success',
  [GUESTRIDE_STATUS.CANCELLED]: 'error',
  [GUESTRIDE_STATUS.FAILED]: 'error',
  [GUESTRIDE_STATUS.DELETED]: 'default',
};

// ─── Canonical status → user-facing label ──────────────────────────────────
//
// Source of truth for what guestride status text the user sees in BOTH the
// dealer portal and the guest portal. Keep this map in sync with the
// guest-portal copy at:
//   guest-portal: src/features/rideshare/constants/guestRideStatus.ts
//
// Any divergence here will cause the two apps to render different text for
// the same raw status, which is exactly what we are reconciling against.
export const STATUS_LABEL_MAP: Record<GuestrideStatus, string> = {
  [GUESTRIDE_STATUS.NEW]: 'Ready to be Called',
  [GUESTRIDE_STATUS.PENDING]: 'Pending',
  [GUESTRIDE_STATUS.BOOKED]: 'Booked',
  [GUESTRIDE_STATUS.DRIVER_ENROUTE]: 'Driver Enroute',
  [GUESTRIDE_STATUS.DRIVER_ARRIVED]: 'Driver Arrived',
  [GUESTRIDE_STATUS.RIDING]: 'Riding',
  [GUESTRIDE_STATUS.COMPLETED]: 'Completed',
  [GUESTRIDE_STATUS.CANCELLED]: 'Cancelled',
  [GUESTRIDE_STATUS.FAILED]: 'Failed',
  [GUESTRIDE_STATUS.DELETED]: 'Deleted',
};

export const PROVIDER_OPTIONS = [
  { value: 'auto', label: 'AUTO' },
  { value: 'lyft', label: 'Lyft' },
  { value: 'uber', label: 'Uber' },
] as const;

export type RideProvider = (typeof PROVIDER_OPTIONS)[number]['value'];

// ─── Status filter → status arrays mapping ──────────────────────────────────

export const STATUS_FILTER_MAP: Record<string, GuestrideStatus[]> = {
  all: [...UPCOMING_STATUSES, ...ACTIVE_STATUSES, ...HISTORY_STATUSES],
  active: [...ACTIVE_STATUSES],
  upcoming: [...UPCOMING_STATUSES],
  completed: [...HISTORY_STATUSES],
  deleted: [...DELETED_STATUSES],
};
